import React, { useEffect, useState } from 'react';
import { Container, Typography, Button, Box, Paper } from '@mui/material';
import { styled } from '@mui/material/styles';
import { motion, useScroll, useTransform } from 'framer-motion';
import { useNavigate, Link as RouterLink } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePageTitle } from '../hooks/usePageTitle';
import SecurityIcon from '@mui/icons-material/Security';
import AssessmentIcon from '@mui/icons-material/Assessment';
import LightbulbIcon from '@mui/icons-material/Lightbulb';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import { DESIGN_TOKENS } from '../theme/designTokens';

const HeroSection = styled(Box)(({ theme }) => ({
  position: 'relative',
  minHeight: '100vh',
  display: 'flex',
  alignItems: 'center',
  background: 'linear-gradient(135deg, #FEFEFE 0%, #F9FAFB 100%)',
  overflow: 'hidden',
  paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.md),
  paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
  // Ultra-wide screens (2560px+)
  '@media (min-width: 2560px)': {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
  },
  // Large desktop screens (1920px+)
  '@media (min-width: 1920px)': {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.lg),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
  },
  [theme.breakpoints.down('lg')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.md),
    minHeight: '90vh',
  },
  [theme.breakpoints.down('md')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
    minHeight: 'auto',
  },
  [theme.breakpoints.down('sm')]: {
    paddingTop: theme.spacing(DESIGN_TOKENS.spacing.section.sm),
    paddingBottom: theme.spacing(DESIGN_TOKENS.spacing.section.md),
    minHeight: 'auto',
  },
}));

const SectionContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '100%',
  maxWidth: '1280px',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: theme.spacing(12),
  flexWrap: 'nowrap', // Prevent wrapping for better control
  // Ultra-wide screens (2560px+)
  '@media (min-width: 2560px)': {
    maxWidth: '1600px',
    gap: theme.spacing(16),
  },
  // Large desktop screens (1920px+)
  '@media (min-width: 1920px)': {
    maxWidth: '1400px',
    gap: theme.spacing(14),
  },
  [theme.breakpoints.down('xl')]: {
    gap: theme.spacing(8),
  },
  // Better breakpoint management for medium-large screens
  '@media (max-width: 1340px)': {
    gap: theme.spacing(6),
  },
  '@media (max-width: 1200px)': {
    flexDirection: 'column',
    gap: theme.spacing(6),
    textAlign: 'center',
  },
  [theme.breakpoints.down('md')]: {
    flexDirection: 'column',
    gap: theme.spacing(4),
    textAlign: 'center',
  },
  [theme.breakpoints.down('sm')]: {
    gap: theme.spacing(3),
  },
}));

const ImageStackContainer = styled(Box)(({ theme }) => ({
  position: 'relative',
  width: '600px',
  height: '500px',
  flexShrink: 0,
  perspective: '800px',
  perspectiveOrigin: 'center center',
  // Ultra-wide screens (2560px+)
  '@media (min-width: 2560px)': {
    width: '800px',
    height: '650px',
    perspective: '1000px',
  },
  // Large desktop screens (1920px+)
  '@media (min-width: 1920px)': {
    width: '700px',
    height: '575px',
    perspective: '900px',
  },
  [theme.breakpoints.down('xl')]: {
    width: '550px',
    height: '450px',
  },
  // Better scaling for medium-large screens
  '@media (max-width: 1340px)': {
    width: '480px',
    height: '400px',
  },
  // Column layout for smaller screens
  '@media (max-width: 1200px)': {
    width: '500px',
    height: '400px',
    margin: '0 auto',
  },
  [theme.breakpoints.down('md')]: {
    width: '420px',
    height: '320px',
    margin: '0 auto',
    transform: 'scale(0.95)',
  },
  [theme.breakpoints.down('sm')]: {
    width: '380px',
    height: '290px',
    transform: 'scale(0.9)',
  },
  [theme.breakpoints.down(480)]: {
    width: '340px',
    height: '260px',
    transform: 'scale(0.85)',
  },
}));

const ImageCard = styled(motion.div)<{ $zIndex: number }>(({ theme, $zIndex }) => ({
  position: 'absolute',
  width: '360px',
  height: '260px',
  transformStyle: 'preserve-3d',
  backgroundColor: '#FEFEFE',
  border: '1px solid #E5E7EB',
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  zIndex: $zIndex,
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&:hover': {
    borderColor: '#4338CA',
  },
  
  '& img': {
    width: '100%',
    height: '100%',
    objectFit: 'cover',
    borderRadius: DESIGN_TOKENS.borderRadius.lg,
  },
  
  // Ultra-wide screens (2560px+)
  '@media (min-width: 2560px)': {
    width: '480px',
    height: '350px',
  },
  // Large desktop screens (1920px+)
  '@media (min-width: 1920px)': {
    width: '420px',
    height: '305px',
  },
  [theme.breakpoints.down('xl')]: {
    width: '320px',
    height: '230px',
  },
  // Better scaling for medium-large screens
  '@media (max-width: 1340px)': {
    width: '280px',
    height: '200px',
  },
  // Column layout for smaller screens
  '@media (max-width: 1200px)': {
    width: '290px',
    height: '210px',
  },
  [theme.breakpoints.down('md')]: {
    width: '230px',
    height: '165px',
  },
  [theme.breakpoints.down('sm')]: {
    width: '200px',
    height: '145px',
  },
  [theme.breakpoints.down(480)]: {
    width: '180px',
    height: '130px',
  },
}));

const TextContent = styled(Box)(({ theme }) => ({
  maxWidth: '600px',
  textAlign: 'left',
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  minHeight: '500px',
  // Ultra-wide screens (2560px+)
  '@media (min-width: 2560px)': {
    maxWidth: '800px',
    minHeight: '650px',
  },
  // Large desktop screens (1920px+)
  '@media (min-width: 1920px)': {
    maxWidth: '700px',
    minHeight: '575px',
  },
  [theme.breakpoints.down('xl')]: {
    minHeight: '450px',
  },
  // Better scaling for medium-large screens
  '@media (max-width: 1340px)': {
    minHeight: '400px',
    maxWidth: '500px',
  },
  // Column layout for smaller screens
  '@media (max-width: 1200px)': {
    textAlign: 'center',
    maxWidth: '100%',
    minHeight: 'auto',
    marginBottom: theme.spacing(3),
    alignItems: 'center',
  },
  [theme.breakpoints.down('md')]: {
    textAlign: 'center',
    maxWidth: '100%',
    minHeight: 'auto',
    marginBottom: theme.spacing(3),
  },
}));

const FeatureCard = styled(motion.div)(({ theme }) => ({
  background: '#FEFEFE',
  border: '1px solid #E5E7EB',
  borderRadius: DESIGN_TOKENS.borderRadius.lg,
  padding: theme.spacing(4),
  textAlign: 'center',
  height: '100%',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  transition: 'all 0.4s cubic-bezier(0.4, 0, 0.2, 1)',
  cursor: 'pointer',
  position: 'relative',
  overflow: 'hidden',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: '4px',
    background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
    transform: 'scaleX(0)',
    transformOrigin: 'left',
    transition: 'transform 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  
  '&:hover': {
    transform: 'translateY(-8px)',
    borderColor: theme.palette.primary.main,
    
    '&::before': {
      transform: 'scaleX(1)',
    },
    
    '& .feature-icon': {
      transform: 'scale(1.1)',
    },
  },
  
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(3),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(2.5),
  },
}));

const IconContainer = styled(Box)(({ theme }) => ({
  width: 80,
  height: 80,
  borderRadius: '50%',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(3),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: -2,
    left: -2,
    right: -2,
    bottom: -2,
    borderRadius: '50%',
    background: `linear-gradient(135deg, ${theme.palette.primary.main}33 0%, ${theme.palette.secondary.main}33 100%)`,
    opacity: 0,
    transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  },
  
  [theme.breakpoints.down('sm')]: {
    width: 64,
    height: 64,
    marginBottom: theme.spacing(2),
  },
}));

const FeaturesContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(DESIGN_TOKENS.spacing.section.lg, 0),
  background: '#FEFEFE',
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(DESIGN_TOKENS.spacing.section.md, 0),
  },
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(DESIGN_TOKENS.spacing.section.sm, 0),
  },
}));

const FeaturesGrid = styled(Box)(({ theme }) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
  gap: theme.spacing(4),
  marginTop: theme.spacing(8),
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr',
    gap: theme.spacing(3),
    marginTop: theme.spacing(6),
  },
}));

const GradientButton = styled(Button)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.secondary.main} 100%)`,
  color: theme.palette.primary.contrastText,
  fontWeight: 600,
  fontSize: '1.125rem',
  padding: theme.spacing(2, 4),
  borderRadius: DESIGN_TOKENS.borderRadius.sm,
  textTransform: 'none',
  boxShadow: DESIGN_TOKENS.shadows.button.default,
  border: 'none',
  position: 'relative',
  overflow: 'hidden',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: '-100%',
    width: '100%',
    height: '100%',
    background: 'linear-gradient(90deg, transparent, rgba(255,255,255,0.2), transparent)',
    transition: 'left 0.5s',
  },
  
  '&:hover': {
    background: `linear-gradient(135deg, ${theme.palette.primary.dark} 0%, ${theme.palette.secondary.dark} 100%)`,
    transform: 'translateY(-2px)',
    boxShadow: DESIGN_TOKENS.shadows.button.hover,
    
    '&::before': {
      left: '100%',
    },
  },
  
  '&:active': {
    transform: 'translateY(0px)',
    boxShadow: DESIGN_TOKENS.shadows.button.default,
  },
  
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: theme.spacing(1.5, 3),
  },
}));

const SecondaryButton = styled(Button)(({ theme }) => ({
  fontSize: '1.125rem',
  padding: theme.spacing(2, 4),
  fontWeight: 500,
  borderColor: '#D1D5DB',
  color: '#374151',
  borderRadius: DESIGN_TOKENS.borderRadius.sm,
  textTransform: 'none',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  position: 'relative',
  
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(135deg, ${theme.palette.primary.main}0A 0%, ${theme.palette.secondary.main}0A 100%)`,
    opacity: 0,
    transition: 'opacity 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    borderRadius: DESIGN_TOKENS.borderRadius.sm,
  },
  
  '&:hover': {
    borderColor: theme.palette.primary.main,
    color: theme.palette.primary.main,
    transform: 'translateY(-1px)',
    
    '&::before': {
      opacity: 1,
    },
  },
  
  '&:active': {
    transform: 'translateY(0px)',
  },
  
  [theme.breakpoints.down('sm')]: {
    fontSize: '1rem',
    padding: theme.spacing(1.5, 3),
  },
}));

const Home = () => {
  const navigate = useNavigate();
  const { scrollY } = useScroll();
  const [animationStarted, setAnimationStarted] = useState(false);
  const { t } = useTranslation();
  
  // Set page title for analytics
  usePageTitle('Home');

  // Parallax transforms for each card
  const y1 = useTransform(scrollY, [0, 1000], [0, -30]);
  const y2 = useTransform(scrollY, [0, 1000], [0, -60]);
  const y3 = useTransform(scrollY, [0, 1000], [0, -90]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setAnimationStarted(true);
    }, 400);
    return () => clearTimeout(timer);
  }, []);

  return (
    <>
      <HeroSection role="region" aria-labelledby="hero-heading">
        <Container maxWidth="xl">
          <SectionContainer>
            <TextContent>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.8, delay: 0.3 }}
              >
                <Typography 
                  id="hero-heading"
                  variant="h1"
                  component="h1" 
                  gutterBottom
                  sx={{ 
                    fontSize: { 
                      xs: '2.2rem', 
                      sm: '2.8rem', 
                      md: '3.2rem', 
                      lg: '3.8rem', 
                      xl: '4rem',
                      // Ultra-wide screens
                      '@media (min-width: 2560px)': '5.5rem',
                      // Large desktop screens
                      '@media (min-width: 1920px)': '4.75rem',
                    },
                    fontWeight: 700,
                    background: 'linear-gradient(135deg, #111827 0%, #374151 100%)',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    marginBottom: { xs: 2, sm: 3 },
                    lineHeight: 1.1,
                  }}
                >
                  {t('home.hero.title')}
                </Typography>
                
                <Typography 
                  variant="h2" 
                  component="h2"
                  paragraph
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: { 
                      xs: '1rem', 
                      sm: '1.15rem', 
                      md: '1.3rem', 
                      lg: '1.4rem', 
                      xl: '1.5rem',
                      // Ultra-wide screens
                      '@media (min-width: 2560px)': '2rem',
                      // Large desktop screens
                      '@media (min-width: 1920px)': '1.75rem',
                    },
                    lineHeight: 1.6,
                    marginBottom: { xs: 3, sm: 4 },
                    fontWeight: 400,
                    textAlign: { 
                      xs: 'center', 
                      '@media (max-width: 1200px)': 'center',
                      lg: 'left' 
                    },
                  }}
                >
                  {t('home.hero.subtitle')}
                </Typography>
                
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, delay: 0.8 }}
                >
                  <Box 
                    sx={{ 
                      display: 'flex', 
                      flexDirection: { xs: 'column', sm: 'row' },
                      gap: { 
                        xs: 2, 
                        sm: 3,
                        // Ultra-wide screens
                        '@media (min-width: 2560px)': 5,
                        // Large desktop screens
                        '@media (min-width: 1920px)': 4,
                      }, 
                      justifyContent: { xs: 'center', lg: 'flex-start' },
                      alignItems: 'center',
                    }}
                  >
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <GradientButton
                        variant="contained"
                        size="large"
                        endIcon={<ArrowForwardIcon />}
                        onClick={() => navigate('/upload')}
                        sx={{ 
                          minWidth: { xs: '200px', sm: '160px' },
                          fontSize: { 
                            xs: '1rem', 
                            sm: '1.125rem',
                            // Ultra-wide screens
                            '@media (min-width: 2560px)': '1.5rem',
                            // Large desktop screens
                            '@media (min-width: 1920px)': '1.3rem',
                          },
                          padding: { 
                            xs: '12px 24px', 
                            sm: '16px 32px',
                            // Ultra-wide screens
                            '@media (min-width: 2560px)': '24px 48px',
                            // Large desktop screens
                            '@media (min-width: 1920px)': '20px 40px',
                          }
                        }}
                      >
                        {t('home.hero.cta')}
                      </GradientButton>
                    </motion.div>
                    
                    <motion.div
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                    >
                      <SecondaryButton
                        variant="outlined"
                        size="large"
                        onClick={() => navigate('/how-it-works')}
                        sx={{ 
                          minWidth: { xs: '200px', sm: '160px' },
                          fontSize: { 
                            xs: '1rem', 
                            sm: '1.125rem',
                            // Ultra-wide screens
                            '@media (min-width: 2560px)': '1.5rem',
                            // Large desktop screens
                            '@media (min-width: 1920px)': '1.3rem',
                          },
                          padding: { 
                            xs: '12px 24px', 
                            sm: '16px 32px',
                            // Ultra-wide screens
                            '@media (min-width: 2560px)': '24px 48px',
                            // Large desktop screens
                            '@media (min-width: 1920px)': '20px 40px',
                          }
                        }}
                      >
                        {t('home.hero.learnMore')}
                      </SecondaryButton>
                    </motion.div>
                  </Box>
                </motion.div>
              </motion.div>
            </TextContent>

            <ImageStackContainer>
              {/* Back card - now using image3 (furthest back) */}
              <ImageCard
                $zIndex={1}
                initial={{ 
                  rotateX: 45,
                  rotateY: -40,
                  rotateZ: -3,
                  translateX: -80,
                  translateY: 60,
                  translateZ: -160,
                  opacity: 0
                }}
                animate={animationStarted ? { 
                  rotateX: 35,
                  rotateY: -30,
                  rotateZ: -2,
                  translateX: -60,
                  translateY: 50,
                  translateZ: -120,
                  opacity: 0.75
                } : {}}
                transition={{ 
                  duration: 0.8,
                  ease: [0.25, 0.46, 0.45, 0.94],
                  delay: 0.1
                }}
                style={{ 
                  left: '0px',
                  top: '150px',
                  y: y1
                }}
              >
                <img src="/images/gemba-image3.png" alt="Workplace Safety Report" />
              </ImageCard>
              
              {/* Middle card with parallax */}
              <ImageCard
                $zIndex={2}
                initial={{ 
                  rotateX: 35,
                  rotateY: 0,
                  rotateZ: 1,
                  translateX: 30,
                  translateY: -15,
                  translateZ: -100,
                  opacity: 0
                }}
                animate={animationStarted ? { 
                  rotateX: 25,
                  rotateY: 5,
                  rotateZ: 1,
                  translateX: 20,
                  translateY: -10,
                  translateZ: -60,
                  opacity: 0.85
                } : {}}
                transition={{ 
                  duration: 0.8,
                  ease: [0.25, 0.46, 0.45, 0.94],
                  delay: 0.2
                }}
                style={{ 
                  left: '130px',
                  top: '110px',
                  y: y2
                }}
              >
                <img src="/images/gemba-image2.png" alt="Safety Assessment Interface" />
              </ImageCard>
              
              {/* Front card - now using image1 (closest) */}
              <ImageCard
                $zIndex={3}
                initial={{ 
                  rotateX: 50,
                  rotateY: 30,
                  rotateZ: 5,
                  translateX: 100,
                  translateY: -60,
                  translateZ: -140,
                  opacity: 0
                }}
                animate={animationStarted ? { 
                  rotateX: 15,
                  rotateY: 20,
                  rotateZ: 2,
                  translateX: 80,
                  translateY: -30,
                  translateZ: 0,
                  opacity: 1
                } : {}}
                transition={{ 
                  duration: 0.8,
                  ease: [0.25, 0.46, 0.45, 0.94],
                  delay: 0.3
                }}
                style={{ 
                  left: '220px',
                  top: '70px',
                  y: y3
                }}
              >
                <img src="/images/gemba-image1.png" alt="EHS Analysis Dashboard" />
              </ImageCard>
            </ImageStackContainer>
          </SectionContainer>
        </Container>
      </HeroSection>

      <FeaturesContainer role="region" aria-labelledby="features-heading">
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-100px" }}
            transition={{ duration: 0.6 }}
          >
            <Typography 
              id="features-heading"
              variant="h2" 
              component="h2" 
              align="center" 
              gutterBottom
              sx={{
                fontSize: { xs: '2rem', sm: '2.5rem', md: '3rem' },
                fontWeight: 700,
                marginBottom: 2
              }}
            >
              {t('home.features.title')}
            </Typography>
            
            <Typography 
              variant="h3" 
              component="h3" 
              align="center" 
              color="text.secondary" 
              paragraph
              sx={{
                fontSize: { xs: '1rem', sm: '1.125rem', md: '1.25rem' },
                maxWidth: '700px',
                margin: '0 auto',
                lineHeight: 1.6
              }}
            >
              {t('home.features.subtitle')}
            </Typography>

            <FeaturesGrid>
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.1 }}
              >
                <FeatureCard>
                  <IconContainer sx={{ background: 'linear-gradient(135deg, #5E6AD2 0%, #8B5CF6 100%)' }}>
                    <SecurityIcon className="feature-icon" sx={{ fontSize: 40, color: '#ffffff' }} aria-hidden="true" />
                  </IconContainer>
                  <Typography variant="h4" component="h4" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('home.features.cards.videoAnalysis.title')}
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {t('home.features.cards.videoAnalysis.description')}
                  </Typography>
                </FeatureCard>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.2 }}
              >
                <FeatureCard>
                  <IconContainer sx={{ background: 'linear-gradient(135deg, #10B981 0%, #34D399 100%)' }}>
                    <AssessmentIcon className="feature-icon" sx={{ fontSize: 40, color: '#ffffff' }} aria-hidden="true" />
                  </IconContainer>
                  <Typography variant="h4" component="h4" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('home.features.cards.assessment.title')}
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {t('home.features.cards.assessment.description')}
                  </Typography>
                </FeatureCard>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.6, delay: 0.3 }}
              >
                <FeatureCard>
                  <IconContainer sx={{ background: 'linear-gradient(135deg, #F59E0B 0%, #FBBF24 100%)' }}>
                    <LightbulbIcon className="feature-icon" sx={{ fontSize: 40, color: '#ffffff' }} aria-hidden="true" />
                  </IconContainer>
                  <Typography variant="h4" component="h4" gutterBottom sx={{ fontWeight: 600 }}>
                    {t('home.features.cards.insights.title')}
                  </Typography>
                  <Typography color="text.secondary" sx={{ lineHeight: 1.6 }}>
                    {t('home.features.cards.insights.description')}
                  </Typography>
                </FeatureCard>
              </motion.div>
            </FeaturesGrid>
          </motion.div>
        </Container>
      </FeaturesContainer>
      <Box sx={{ pb: 12, background: '#FEFEFE' }}>
        <Container>
          <Paper 
            sx={{ 
              p: { xs: 3, sm: 6 }, 
              textAlign: 'center',
              borderRadius: 4,
              background: 'linear-gradient(135deg, rgba(94, 106, 210, 0.04) 0%, rgba(139, 92, 246, 0.04) 100%)',
              border: '1px solid rgba(94, 106, 210, 0.1)',
            }}
          >
            <Typography variant="h2" component="h2" gutterBottom sx={{ fontSize: '2rem', mb: 3, fontWeight: 700 }}>
              {t('home.cta.title')}
            </Typography>
            <Typography variant="body1" sx={{ color: '#6B7280', maxWidth: '600px', mx: 'auto', mb: 4, lineHeight: 1.6 }}>
              {t('home.cta.subtitle')}
            </Typography>
            <motion.div whileHover={{ scale: 1.02 }} whileTap={{ scale: 0.98 }}>
              <Button 
                variant="contained" 
                size="large"
                component={RouterLink}
                to="/upload"
                sx={{ 
                  px: 4,
                  py: 2,
                  fontSize: '1.1rem',
                  fontWeight: 600,
                  background: 'linear-gradient(135deg, #5E6AD2 0%, #8B5CF6 100%)',
                  '&:hover': {
                    background: 'linear-gradient(135deg, #4C5BC7 0%, #7C3AED 100%)',
                  }
                }}
              >
                {t('home.cta.button')}
              </Button>
            </motion.div>
          </Paper>
        </Container>
      </Box>
    </>
  );
};

export default Home; 